"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import * as Sentry from "@sentry/browser";

/**
 * Canonical contact block — used by the contact panel, the product footer and
 * the corporate page so contact methods never drift between surfaces.
 *
 * Email is shown as visible text with an explicit Copy button beside it —
 * mailto: alone does nothing on desktops without a configured mail client
 * (expected browser behavior, so we design around it instead of relying on
 * it). Clicking the address opens a small anchored "choose how to email us"
 * menu (Gmail / Outlook / default mail app / copy) instead of firing a bare
 * mailto: — a site cannot trigger the OS-level mail-app picker, so this menu
 * is the standard web equivalent. mailto: remains available inside the menu
 * as the explicit "default mail app" option.
 *
 * Standard corporate pattern for the phone number: show the number once and
 * offer three explicit actions beside it instead of one "smart" link —
 * WhatsApp first (genuinely cross-platform: wa.me opens WhatsApp Web on
 * desktop, the app on mobile), then Call (tel: — native on mobile; desktop
 * browsers ignore it or hand off to a configured calling app, which is
 * expected tel: behavior industry-wide), then Text (sms: — mobile-only by
 * nature). Recognizable icons make each option obvious on any device.
 */

const PHONE_DISPLAY = "+254 713 487 160";
const PHONE_E164 = "+254713487160";
const WA_NUMBER = "254713487160";
export const EMAIL = "hello@pisoque.co.ke";

function Icon({ d, label }: { d: string; label?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="currentColor"
      aria-hidden={label ? undefined : true}
      role={label ? "img" : undefined}
      aria-label={label}
    >
      <path d={d} />
    </svg>
  );
}

// Standard glyphs (Simple Icons WhatsApp; Material call / chat / mail).
const PATH_WHATSAPP =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.297-.497.1-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z";
const PATH_CALL =
  "M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z";
const PATH_SMS =
  "M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM7 9h10v2H7V9z";
const PATH_MAIL =
  "M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z";
const PATH_COPY =
  "M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z";
const PATH_CHECK = "M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z";
// Brand glyphs from simple-icons (gmail.svg / microsoftoutlook.svg, CC0).
const PATH_GMAIL =
  "M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z";
const PATH_OUTLOOK =
  "M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12 16.08V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z";

/**
 * Copies text to the clipboard. Falls back to the legacy textarea trick for
 * non-secure contexts / older browsers where navigator.clipboard is missing.
 */
async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  document.execCommand("copy");
  document.body.removeChild(ta);
}

function CopyEmailButton() {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    try {
      await copyToClipboard(EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // A broken contact CTA is exactly the kind of failure we want to see
      // in monitoring. captureException is a no-op if Sentry isn't set up.
      Sentry.captureException(err);
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-live="polite"
      className={`neo inline-flex min-h-[32px] items-center gap-1.5 rounded-xl2 px-2.5 text-sm font-semibold transition-colors ${
        copied ? "glass text-accent-text" : "glass text-muted hover:text-accent-text"
      }`}
    >
      <Icon d={copied ? PATH_CHECK : PATH_COPY} label={copied ? "Copied" : "Copy email address"} />
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

/**
 * "Choose how to email us" menu — small popover anchored to the email
 * address, NOT a new window/tab and NOT a full-screen takeover. Only the
 * option the visitor picks navigates: Gmail and Outlook open compose in a
 * new TAB (target="_blank" — browsers open _blank anchors as tabs;
 * rel="noopener noreferrer" so the new tab gets no window.opener handle),
 * "default mail app" is the plain mailto: fallback, and Copy reuses the
 * same copyToClipboard helper as the Copy button (no duplicated logic).
 * Dismissed by: choosing an option, clicking outside, or Escape.
 */
export function EmailMenu({
  subject,
  renderTrigger,
  wrapClassName = "relative inline-flex",
}: {
  /** Optional subject line threaded into all three compose targets. */
  subject?: string;
  /** Custom trigger (a CTA button, an inline link). Defaults to the mail
   *  icon + email address used by the canonical contact block. */
  renderTrigger?: (args: { open: boolean; toggle: () => void }) => ReactNode;
  /** Class on the positioning wrapper — e.g. "relative block w-full" so a
   *  full-width CTA button fills its column. */
  wrapClassName?: string;
} = {}) {
  const [open, setOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  function toggle() {
    if (!open && wrapRef.current) {
      // Anchored below by default; flip above when the trigger sits near the
      // bottom of the viewport (the footer case) so the menu stays on-screen.
      const r = wrapRef.current.getBoundingClientRect();
      setDropUp(window.innerHeight - r.bottom < 240);
    }
    setCopied(false);
    setOpen((v) => !v);
  }

  // Outside click + Escape close the menu while it is open.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        // Capture-phase + stopPropagation: Escape closes just this menu, not
        // also the contact side panel listening on the same key underneath.
        e.stopPropagation();
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  async function handleCopy() {
    try {
      await copyToClipboard(EMAIL);
      setCopied(true);
      // Leave the confirmation visible briefly, then close with the menu.
      setTimeout(() => {
        setCopied(false);
        setOpen(false);
      }, 1200);
    } catch (err) {
      Sentry.captureException(err);
    }
  }

  const itemCls =
    "flex min-h-[44px] w-full items-center gap-2.5 rounded-lg px-3 text-left text-sm " +
    "font-medium text-text transition-colors hover:bg-[var(--border)] hover:text-accent-text";

  // Compose targets. A subject (when given) is threaded into each provider's
  // own parameter: Gmail uses &su, Outlook and mailto use subject.
  const su = subject ? encodeURIComponent(subject) : "";
  const gmailHref = `https://mail.google.com/mail/?view=cm&fs=1&to=${EMAIL}${su ? `&su=${su}` : ""}`;
  const outlookHref = `https://outlook.live.com/mail/0/deeplink/compose?to=${EMAIL}${su ? `&subject=${su}` : ""}`;
  const mailtoHref = `mailto:${EMAIL}${su ? `?subject=${su}` : ""}`;

  // Wrapper and menu are <span>s (not <div>s) so the menu is valid phrasing
  // content when a caller drops the trigger inline inside a <p> — an
  // absolutely-positioned <div> there would auto-close the paragraph.
  return (
    <span ref={wrapRef} className={wrapClassName}>
      {renderTrigger ? (
        renderTrigger({ open, toggle })
      ) : (
        <button
          type="button"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          className="inline-flex min-h-[44px] items-center gap-2.5 text-base text-muted transition-colors hover:text-accent-text"
        >
          <span className="text-accent-text"><Icon d={PATH_MAIL} /></span>
          {EMAIL}
        </button>
      )}

      {open && (
        <span
          role="menu"
          aria-label="Choose how to email us"
          className={`glass absolute left-0 z-50 block w-64 rounded-xl2 p-1.5 shadow-xl ${
            dropUp ? "bottom-full mb-2" : "top-full mt-2"
          }`}
          style={{ background: "var(--surface-solid)" }}
        >
          <a
            role="menuitem"
            className={itemCls}
            href={gmailHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
          >
            <span className="text-accent-text"><Icon d={PATH_GMAIL} label="Gmail" /></span>
            Open in Gmail
          </a>
          <a
            role="menuitem"
            className={itemCls}
            href={outlookHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
          >
            <span className="text-accent-text"><Icon d={PATH_OUTLOOK} label="Outlook" /></span>
            Open in Outlook
          </a>
          <a
            role="menuitem"
            className={itemCls}
            href={mailtoHref}
            onClick={() => setOpen(false)}
          >
            <span className="text-accent-text"><Icon d={PATH_MAIL} label="Default mail app" /></span>
            Use my default mail app
          </a>
          <button
            type="button"
            role="menuitem"
            className={itemCls}
            onClick={handleCopy}
            aria-live="polite"
          >
            <span className="text-accent-text">
              <Icon d={copied ? PATH_CHECK : PATH_COPY} label={copied ? "Copied" : "Copy email address"} />
            </span>
            {copied ? "Copied!" : "Copy email address"}
          </button>
        </span>
      )}
    </span>
  );
}

function ActionLink({
  href,
  primary = false,
  children,
}: {
  href: string;
  primary?: boolean;
  children: ReactNode;
}) {
  const base =
    "neo inline-flex min-h-[44px] items-center gap-2 rounded-xl2 px-4 text-base font-semibold transition-colors";
  const style = primary
    ? "bg-accent text-accent-ink"
    : "glass text-text hover:text-accent-text";
  return (
    <a className={`${base} ${style}`} href={href}>
      {children}
    </a>
  );
}

export function ContactActions({ showSite = true }: { showSite?: boolean }) {
  return (
    <div className="flex flex-col gap-4">
      {/* Email — address opens the "choose how to email us" menu; the Copy
          button beside it stays as the one-click fast path. */}
      <div className="flex flex-wrap items-center gap-2.5">
        <EmailMenu />
        <CopyEmailButton />
      </div>

      {/* Phone number once, three explicit ways to use it. */}
      <div>
        <p className="flex min-h-[32px] items-center gap-2.5 text-base text-text">
          <span className="text-accent-text"><Icon d={PATH_CALL} /></span>
          <span className="font-semibold">{PHONE_DISPLAY}</span>
        </p>
        <div className="mt-2.5 flex flex-wrap gap-2.5">
          <ActionLink href={`https://wa.me/${WA_NUMBER}`} primary>
            <Icon d={PATH_WHATSAPP} label="WhatsApp" />
            WhatsApp
          </ActionLink>
          <ActionLink href={`tel:${PHONE_E164}`}>
            <Icon d={PATH_CALL} label="Call" />
            Call
          </ActionLink>
          <ActionLink href={`sms:${PHONE_E164}`}>
            <Icon d={PATH_SMS} label="Text message" />
            Text
          </ActionLink>
        </div>
      </div>

      {showSite && (
        <a
          className="inline-flex min-h-[44px] items-center text-base text-muted transition-colors hover:text-accent-text"
          href="https://pisoque.co.ke"
        >
          pisoque.co.ke
        </a>
      )}
    </div>
  );
}
